import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GraduationCap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing or invalid reset link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-9 w-9 rounded-lg bg-[#0A192F] flex items-center justify-center"><GraduationCap className="h-5 w-5 text-amber-500" /></div>
          <span className="font-heading font-bold text-[#0A192F]">Insurance License Lab</span>
        </Link>

        {done ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="reset-done">
            <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
            <h2 className="font-heading text-xl font-bold text-[#0A192F]">Password updated</h2>
            <p className="text-sm text-slate-500 mt-2">You can now log in with your new password.</p>
            <Button className="mt-6 w-full bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate("/login")} data-testid="reset-to-login-btn">Go to log in</Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <h2 className="font-heading text-2xl font-bold text-[#0A192F]">Set a new password</h2>
            <p className="text-sm text-slate-500 mt-1">Choose a strong password you'll remember.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="password">New password</Label>
                <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="reset-password-input" />
                <p className="text-xs text-slate-400 mt-1">At least 6 characters</p>
              </div>
              <div>
                <Label htmlFor="confirm">Confirm password</Label>
                <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={loading || !token} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-11" data-testid="reset-submit-btn">
                {loading ? "Updating…" : "Update password"}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
